import React, { useEffect, useRef, useState } from 'react';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import * as yup from 'yup';

import api from '../../../../services/api';
import { useToast } from '../../../../hooks/toast';
import Input from '../../../../components/Input';
import Select from '../../../../components/Selects/SimpleSelect';
import PrimaryButton from '../../../../components/Buttons/PrimaryButton';
import SecondaryButton from '../../../../components/Buttons/SecondaryButton';
import { ArrowBackIcon, CrossIcon } from '../../../../assets/svg/SVGComponents';

import {
  AddArenaButton,
  ArenaCard,
  ArenaCardHeader,
  FieldGroup,
  FieldLabel,
  FieldRow,
  FormButtonsRow,
  RemoveArenaButton,
} from './StepArenas.styles';
import { FormCard, StepSubtitle, StepTitle } from '../styles';
import { ITournamentSetupData, MODALITIES, MODALITY_SLUG_TO_NAME } from '..';

const arenaItemSchema = yup.object().shape({
  name: yup.string().required('É necessário adicionar um título'),
});

interface IArenaEditItem {
  id: string;
  name: string;
  modality: string;
  judgeId: string;
  titleErrorMessage?: string;
}

interface IAvailableJudge {
  id: string;
  name: string;
  email: string;
  avatar_url: string | null;
}

interface IJudgeRecord {
  id: string;
  tournament_id: string;
  user_id: string;
  created_at: string;
  user: Pick<IAvailableJudge, 'id' | 'name' | 'email' | 'avatar_url'>;
}

interface IStepArenasProps {
  tournamentData: ITournamentSetupData;
  onSave: (tournament: ITournamentSetupData) => void;
  onCancel: () => void;
}

const StepArenas: React.FC<IStepArenasProps> = ({
  tournamentData,
  onSave,
  onCancel,
}) => {
  const { addToast } = useToast();
  const formRef = useRef<FormHandles>(null);

  const [arenas, setArenas] = useState<IArenaEditItem[]>(() => {
    const modalities = tournamentData.modalities ?? [];
    if (modalities.length > 0) {
      return modalities.flatMap(mod =>
        (mod.arenas ?? []).map(arena => ({
          id: arena.id,
          name: arena.name,
          modality: mod.modality,
          judgeId: '',
        })),
      );
    }
    return [{ id: '', name: '', modality: '', judgeId: '' }];
  });

  const [availableJudges, setAvailableJudges] = useState<IAvailableJudge[]>([]);
  const [assignedJudgeIds, setAssignedJudgeIds] = useState<Set<string>>(
    new Set(),
  );
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingArenaId, setDeletingArenaId] = useState<string | null>(null);
  const [loadingJudges, setLoadingJudges] = useState(false);

  useEffect(() => {
    if (!tournamentData.id) return;
    setLoadingJudges(true);
    Promise.all([
      api.get<IJudgeRecord[]>(`/tournaments/${tournamentData.id}/judges`),
      api.get<IAvailableJudge[]>(
        `/tournaments/${tournamentData.id}/judges/available`,
      ),
    ])
      .then(([assignedRes, availableRes]) => {
        setAssignedJudgeIds(
          new Set((assignedRes.data ?? []).map(j => j.user_id)),
        );
        setAvailableJudges(availableRes.data ?? []);
      })
      .catch(() =>
        addToast({
          type: 'error',
          expression: 'Erro!',
          description: 'Não foi possível carregar a lista de juízes.',
        }),
      )
      .finally(() => setLoadingJudges(false));
  }, [tournamentData.id]);

  const handleTituloChange = (index: number, value: string) => {
    setArenas(prev =>
      prev.map((arena, i) => (i === index ? { ...arena, name: value } : arena)),
    );
  };

  const addArena = () => {
    setArenas(prev => [
      ...prev,
      { id: '', name: '', modality: '', judgeId: '' },
    ]);
  };

  const removeArena = async (index: number) => {
    const arena = arenas[index];

    if (!arena.id) {
      setArenas(prev => prev.filter((_, i) => i !== index));
      return;
    }

    try {
      setDeletingArenaId(arena.id);
      await api.delete(`/tournaments/${tournamentData.id}/arenas/${arena.id}`);
      setArenas(prev => prev.filter((_, i) => i !== index));
    } catch {
      addToast({
        type: 'error',
        expression: 'Erro!',
        description: 'Não foi possível excluir a arena. Tente novamente.',
      });
    } finally {
      setDeletingArenaId(null);
    }
  };

  const handleSubmit = async () => {
    const fieldErrors: Record<string, string> = {};
    let hasError = false;

    for (let i = 0; i < arenas.length; i++) {
      try {
        await arenaItemSchema.validate(arenas[i], { abortEarly: true });
      } catch (err) {
        hasError = true;
        fieldErrors[`arena_${i}_name`] = (err as yup.ValidationError).message;
      }
    }

    formRef.current?.setErrors(fieldErrors);

    if (hasError) {
      setFormError('Preencha todos os campos obrigatórios.');
      return;
    }
    setFormError('');

    try {
      setIsSubmitting(true);

      const { data: tournamentDataUpdate } = await api.put(
        `/tournaments/${tournamentData.id}/arenas`,
        {
          arenas: arenas.map(arena => ({
            arena_id: arena.id,
            modality_slug: arena.modality,
            name: arena.name,
            judge_id: arena.judgeId || null,
          })),
        },
      );

      const newJudgeIds = arenas
        .map(a => a.judgeId)
        .filter(id => id && !assignedJudgeIds.has(id));

      const uniqueNewJudgeIds = [...new Set(newJudgeIds)];

      if (uniqueNewJudgeIds.length > 0) {
        await api.put(`/tournaments/${tournamentData.id}/judges`, {
          action: 'add',
          judge_ids: uniqueNewJudgeIds,
        });
      }

      onSave(tournamentDataUpdate);
    } catch {
      addToast({
        type: 'error',
        expression: 'Erro!',
        description: 'Não foi possível salvar as arenas. Tente novamente.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const availableModalities = tournamentData.modalities?.length
    ? [...new Set(tournamentData.modalities.map(m => m.modality))]
    : MODALITIES.map(m => m.slug);

  const judgeOptions = availableJudges.map(u => ({
    value: u.id,
    label: u.name,
  }));

  return (
    <FormCard>
      <StepTitle>Etapa 2: Arenas</StepTitle>
      {formError && <StepSubtitle>{formError}</StepSubtitle>}

      <Form
        ref={formRef}
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onSubmit={() => {}}
        initialData={arenas.reduce(
          (acc, a, i) => ({ ...acc, [`arena_${i}_name`]: a.name }),
          {},
        )}
      >
        {arenas.map((arena, index) => (
          <ArenaCard key={arena.id || `new_${index}`}>
            <ArenaCardHeader>
              <RemoveArenaButton
                type="button"
                onClick={() => removeArena(index)}
                aria-label="Remover arena"
                disabled={deletingArenaId === arena.id && !!arena.id}
              >
                <CrossIcon
                  width="0.85rem"
                  stroke="currentColor"
                  fill="none"
                  strokeWidth={2.5}
                />
              </RemoveArenaButton>
            </ArenaCardHeader>
            <FieldRow>
              <FieldGroup>
                <FieldLabel>Nome da Arena</FieldLabel>
                <Input
                  name={`arena_${index}_name`}
                  placeholder="Ex: Arena 1 - Robô Pintor"
                  onChange={e => handleTituloChange(index, e.target.value)}
                  readOnly={isSubmitting}
                />
              </FieldGroup>

              <FieldGroup>
                <FieldLabel>Modalidade</FieldLabel>
                <Select
                  name={`arena_${index}_modality`}
                  options={availableModalities.map(slug => ({
                    value: slug,
                    label: MODALITY_SLUG_TO_NAME[slug] ?? slug,
                  }))}
                  value={
                    arena.modality
                      ? {
                          value: arena.modality,
                          label:
                            MODALITY_SLUG_TO_NAME[arena.modality] ??
                            arena.modality,
                        }
                      : null
                  }
                  styles={{
                    control: (base: object) => ({
                      ...base,
                      height: '3.05rem',
                      minHeight: '3.05rem',
                      borderRadius: '0.56rem',
                    }),
                  }}
                  onChange={(
                    option: { value: string; label: string } | null,
                  ) => {
                    setArenas(prev =>
                      prev.map((a, i) =>
                        i === index
                          ? { ...a, modality: option?.value ?? '' }
                          : a,
                      ),
                    );
                  }}
                  isClearable
                  placeholder="Modalidade"
                />
              </FieldGroup>

              <FieldGroup>
                <FieldLabel>Juíz</FieldLabel>
                <Select
                  name={`arena_${index}_judge`}
                  options={judgeOptions}
                  value={
                    arena.judgeId
                      ? (judgeOptions.find(o => o.value === arena.judgeId) ??
                        null)
                      : null
                  }
                  styles={{
                    control: (base: object) => ({
                      ...base,
                      height: '3.05rem',
                      minHeight: '3.05rem',
                      borderRadius: '0.56rem',
                    }),
                    menuPortal: (base: object) => ({ ...base, zIndex: 9999 }),
                  }}
                  onChange={(
                    option: { value: string; label: string } | null,
                  ) => {
                    setArenas(prev =>
                      prev.map((a, i) =>
                        i === index
                          ? { ...a, judgeId: option?.value ?? '' }
                          : a,
                      ),
                    );
                  }}
                  isDisabled={loadingJudges}
                  isClearable
                  placeholder={
                    loadingJudges ? 'Carregando...' : 'Selecionar juíz'
                  }
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuPlacement="auto"
                />
              </FieldGroup>
            </FieldRow>
          </ArenaCard>
        ))}

        <AddArenaButton type="button" onClick={addArena}>
          + Adicionar mais arenas
        </AddArenaButton>

        <FormButtonsRow>
          <SecondaryButton type="button" onClick={onCancel}>
            <ArrowBackIcon
              width="1.22rem"
              fill="none"
              stroke="#323232"
              strokeWidth={2}
            />
            Voltar
          </SecondaryButton>
          <PrimaryButton
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {isSubmitting ? 'Salvando...' : 'Salvar'}
            <ArrowBackIcon
              width="1.22rem"
              fill="none"
              stroke="#fff"
              strokeWidth={2}
              style={{ transform: 'rotate(180deg)' }}
            />
          </PrimaryButton>
        </FormButtonsRow>
      </Form>
    </FormCard>
  );
};
export { StepArenas };
